
import { AuditModel, FileDownloadModel, PermissionTypeEnum, ProcessTypeEnum, TenantUserModel } from '@/libs/Api';
import { CrudAction } from '@/libs/core/+state/models/crud-action';
import { CrudDataStats } from '@/libs/core/+state/models/crud-data-stats';
import { CrudGetter } from '@/libs/core/+state/models/crud-getter';
import { createCrudQueryPayload } from '@/libs/core/+state/models/crud-query-payload';
import { CrudQueryPredicate } from '@/libs/core/+state/models/crud-query-predicate';
import { auditsStore } from '@/libs/audits/+state/store';
import Page from '@/Page.vue';
import { Component, Prop, Ref, Watch } from 'vue-property-decorator';
import AuditCreateDialog from '@/libs/audits/components/AuditCreateDialog.vue';
import WorkTaskCreateDialog from '@/libs/work-tasks/components/WorkTaskCreateDialog.vue';
import { authStore } from '@/libs/auth/+store/store';
import { AuthGetter } from '@/libs/auth/models/auth-state';
import ApiService from '@/libs/core/api-service';
import CategoryTypeSelect from '@/libs/common/components-smart/CategoryTypeSelect.vue';

const b64toBlob = (b64Data: string, contentType = '', sliceSize = 512): Blob => {
    const byteCharacters = atob(b64Data);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
        const slice = byteCharacters.slice(offset, offset + sliceSize);

        const byteNumbers = new Array(slice.length);
        for (let i = 0; i < slice.length; i++) {
            byteNumbers[i] = slice.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        byteArrays.push(byteArray);
    }

    const blob = new Blob(byteArrays, { type: contentType });
    return blob;
};

@Component({
    components: {
        CategoryTypeSelect,
        AuditCreateDialog,
        WorkTaskCreateDialog,
    },
})
export default class Audits extends Page {
    filterCategoryTypeId: ProcessTypeEnum | null = null;
    filterCategoryId: string | null = null;
    filterActive = true;
    mobileFiltersShow = false;
    pageIndex = 1;
    pageSize = 25;
    pagingChanged = false;
    workTaskAuditId: string | null = null;
    tenantUser: TenantUserModel = authStore.useGetter(AuthGetter.GetTenantUser) as TenantUserModel;
    @Prop() tenantId!: string;
    @Prop() partyId!: string;
    @Ref() dialog!: AuditCreateDialog;
    @Ref() workTaskCreateDialog!: WorkTaskCreateDialog;
    protected ProcessTypeEnum: typeof ProcessTypeEnum = ProcessTypeEnum;
    protected PermissionTypeEnum: typeof PermissionTypeEnum = PermissionTypeEnum;

    // ✅ NOVÝ SYSTÉM: Kontroluje, zda může uživatel vytvořit audit (podle kategorií)
    get canCreateAudit(): boolean {
        // Pokud má přístup ke všem kategoriím, zkontroluj globální oprávnění
        if (this.tenantUser.accessAllCategories) {
            const permissions = authStore.useGetter(AuthGetter.GetPermissions) as PermissionTypeEnum[];
            return permissions.contains(PermissionTypeEnum.EditAllAudits) || permissions.contains(PermissionTypeEnum.EditOwnedAudit);
        }

        // Pokud nemá přístup ke všem, zkontroluj, zda má alespoň jednu kategorii s těmito oprávněními
        const accessible = this.getUserAccessibleCategoryIds([
            PermissionTypeEnum.EditAllAudits,
            PermissionTypeEnum.EditOwnedAudit,
        ]);
        return accessible.length > 0;
    }

    get totalRows(): number {
        const stats = auditsStore.useGetter(CrudGetter.DataStats, 'Audits') as CrudDataStats;
        return stats?.rowCount ?? 0;
    }

    get headers() {
        return [
            { text: '', value: 'id', align: 'left', sortable: false, width: 30 },
            { text: this.$i18n.t('audits.name'), value: 'name', align: 'left', sortable: false },
            { text: this.$i18n.t('categories.processType'), value: 'processType', align: 'left', sortable: false },
            { text: this.$i18n.t('audits.categoryName'), value: 'category.name', align: 'left', sortable: false },
            { text: this.$i18n.t('audits.owner'), value: 'owner.user.displayName', align: 'left', sortable: false },
            { text: '', value: 'id', align: 'left', sortable: false, width: '0px' },
        ];
    }

    get loading(): boolean {
        return auditsStore.useGetter(CrudGetter.Pending, 'Audits');
    }

    get audits(): AuditModel[] {
        console.log([...auditsStore.useGetter(CrudGetter.Data, 'Audits')]);
        return [...auditsStore.useGetter(CrudGetter.Data, 'Audits')];
    }

    @Watch('filterCategoryTypeId')
    @Watch('filterCategoryId')
    @Watch('filterActive')
    filtersChanged() {
        this.pageIndex = 1;
        this.load();
    }

    @Watch('pageIndex')
    @Watch('pageSize')
    paginationChanged() {
        this.saveQuery();
        if (this.pagingChanged) {
            this.pagingChanged = false;
            return;
        }
        this.load();
    }

    mounted() {
        if (this.getQueryInt('pageSize') != null) {
            this.pageSize = this.getQueryInt('pageSize') ?? 25;
        } else {
            if (window.localStorage.ptPageSize && Number(window.localStorage.ptPageSize)) {
                this.pageSize = Number(window.localStorage.ptPageSize);
            }
            this.saveQuery();
        }
        this.load();
    }

    load() {
        auditsStore.dispatch(CrudAction.GetAll, this.getPayload());
    }

    getFilters(): CrudQueryPredicate[] {
        let filters: CrudQueryPredicate[] = [];
        
        // Nový systém: filtrovat kategorie s kontextovými oprávněními
        if (!this.tenantUser.accessAllCategories) {
            const accessible = this.getUserAccessibleCategoryIds([
                PermissionTypeEnum.Admin,
                PermissionTypeEnum.PerformAudit,
                PermissionTypeEnum.EditAllAudits,
                PermissionTypeEnum.Audits,
            ]);
            
            if (accessible.length > 0) {
                filters.push({
                    field: 'categoryId',
                    op: 'in',
                    comparand: accessible,
                });
            } else {
                // Žádný přístup = prázdný výsledek
                filters.push({ field: 'id', op: 'eq', comparand: '00000000-0000-0000-0000-000000000000' });
            }
        }
        
        if (this.filterCategoryTypeId) {
            filters.push({ field: 'category.processType', op: 'eq', comparand: this.filterCategoryTypeId });
        }
        if (this.filterCategoryId) {
            filters.push({ field: 'categoryId', op: 'eq', comparand: this.filterCategoryId });
        }
        filters.push({ field: 'isActive', op: 'eq', comparand: this.filterActive });
        return filters;
    }

    getPayload() {
        return createCrudQueryPayload<AuditModel>({ field: 'name', index: 1, order: 'asc' }, this.getFilters(), {
            pageIndex: this.pageIndex,
            pageSize: this.pageSize,
        }, false, 'Audits');
    }

    createItemClick() {
        this.dialog.open();
    }

    exportClick() {
        const filters = this.getFilters();
        auditsStore.dispatch(
            CrudAction.Download,
            createCrudQueryPayload<AuditModel>(
                {
                    field: 'name',
                    index: 1,
                    order: 'asc',
                },
                filters
            )
        );
    }

    saveQuery() {
        this.setQuery('pageSize', this.pageSize?.toString());
        window.localStorage.ptPageSize = this.pageSize?.toString();
    }

    planWorkTask(audit: AuditModel) {
        this.workTaskAuditId = audit.id!;
        this.workTaskCreateDialog.open();
    }

    printAudit(audit: AuditModel) {
        //this.printing = true;
        ApiService.api
            .printAuditById(audit!.id!)
            .then((e: any) => {
                const res = e.data.returnValue as FileDownloadModel;
                const blob = b64toBlob(res.base64!, res.contentType!);
                const tab = window.open();
                tab!.location.href = window.URL.createObjectURL(blob);
            })
            .catch((e: any) => {
                //this.printing = false;
                throw e;
            });
    }
}
